/**
 * `W5-T09` §4.7, AC 32, 36 — a document's page as rows: the same rows, in the same groups and order,
 * with the same labels, as the server's `documentLines` (`apps/api/src/modules/payments/documents/
 * document-lines.ts`), so the screen and the PDF read alike.
 *
 * Banner; header (title, number, issue date, date of the operation, issuer, recipient); then a
 * receipt's billed figures, the payment group under its information heading and the line that the
 * professional's share is not invoiced; or a statement's payment group, its transfer and the line
 * that it is not an invoice. Each group is its own array, so the €90 never sits among billed rows.
 *
 * Every figure is the server's `Money`, printed by `formatMoney`; the rate is `formatRateBps` (AC 38).
 * Dates are Madrid calendar dates, `dateStyle: 'long'`, as on the server (run record, Deviation 9).
 */
import { type Money, type MoneyDocument } from '@marketplace/contracts';
import { i18next } from '../../../i18n/index.js';
import { formatMoney, formatRateBps } from '../../checkout/money.js';

export type DocumentLocale = 'es' | 'en';

export type DocumentRow =
  | { type: 'heading'; key: string; text: string }
  | { type: 'row'; key: string; label: string; value: string }
  | { type: 'note'; key: string; text: string };

export interface DocumentRows {
  banner: string;
  groups: DocumentRow[][];
}

export function documentDate(iso: string, locale: DocumentLocale): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'Europe/Madrid' }).format(
    new Date(iso),
  );
}

export function documentRows(doc: MoneyDocument, locale: DocumentLocale): DocumentRows {
  const t = i18next.getFixedT(locale);
  const heading = (key: string): DocumentRow => ({ type: 'heading', key, text: t(key) });
  const row = (key: string, value: string, values?: Record<string, string>): DocumentRow => ({
    type: 'row',
    key,
    label: t(key, values),
    value,
  });
  const note = (key: string, values?: Record<string, string>): DocumentRow => ({
    type: 'note',
    key,
    text: t(key, values),
  });
  const money = (amount: Money) => formatMoney(amount, locale);
  const rate = (bps: number) => ({ rate: formatRateBps(bps, locale) });

  const notConfigured = t('documents.issuer.notConfigured');
  const { name, taxId, address } = doc.issuer;
  const header = [
    heading(
      doc.kind === 'PAYOUT_STATEMENT'
        ? 'documents.title.statement'
        : doc.receiptType === 'simplified_invoice'
          ? 'documents.title.simplifiedInvoice'
          : 'documents.title.receipt',
    ),
    row('documents.number', doc.number),
    row('documents.issuedAt', documentDate(doc.issuedAt, locale)),
    row('documents.occurredAt', documentDate(doc.occurredAt, locale)),
    row(
      'documents.issuer',
      [name, taxId, address].map((part) => part ?? notConfigured).join(' · '),
    ),
    row('documents.recipient', doc.recipient.name ?? '—'),
  ];

  const payment = (headingKey: string): DocumentRow[] => [
    heading(headingKey),
    row('documents.payment.fee', money(doc.payment.fee)),
    row('documents.payment.providerShare', money(doc.payment.providerShare)),
    row('documents.payment.platformFee', money(doc.payment.platformFee)),
    row('documents.payment.iva', money(doc.payment.iva), rate(doc.payment.ivaRateBps)),
    row('documents.payment.total', money(doc.payment.clientPaid)),
    note('documents.payment.ivaNote'),
  ];

  const body =
    doc.kind === 'PLATFORM_FEE_RECEIPT'
      ? [
          [
            row('documents.receipt.base', money(doc.base)),
            row('documents.receipt.iva', money(doc.iva), rate(doc.ivaRateBps)),
            row('documents.receipt.total', money(doc.total)),
          ],
          payment('documents.receipt.infoHeading'),
          [
            note('documents.receipt.providerShareNotInvoiced', {
              amount: money(doc.payment.providerShare),
            }),
          ],
        ]
      : [
          payment('documents.statement.paymentHeading'),
          [
            row('documents.statement.transferred', money(doc.transferred)),
            row('documents.statement.transferRef', doc.transferRef),
          ],
          [note('documents.statement.notInvoice')],
        ];

  return { banner: t('documents.provisional.banner'), groups: [header, ...body] };
}
